import React, { memo, useEffect, useRef, useState } from "react";
import { useOutsideAlerter, capitalizeAll } from "../../utils/helper";
import filterIcon from "../../assets/images/filter.svg";
import filterDateLogo from "../../assets/admin Images/filter-date-logo.svg";
import SearchSelect from "./SearchSelect";
import { useDispatch, useSelector } from "react-redux";
import { getActiveEmployers, getProductList } from "../../redux/action";
import moment from "moment";

const NPS_CATEGORIES = [
  { label: "Promoter", value: "Promoter" },
  { label: "Passive", value: "Passive" },
  { label: "Detractor", value: "Detractor" },
];

const DSAT_CSAT_OPTIONS = [
  { label: "All", value: "all" },
  { label: "DSAT only", value: "dsat" },
  { label: "CSAT only", value: "csat" },
];

const PlatformSurveyFilterBtn = memo(
  ({ setFilters, activeFilters, showNpsCategory, showDsatCsatDropdown }) => {
    const dispatch = useDispatch();
    const fromDateRef = useRef();
    const toDateRef = useRef();
    const [showFilter, setShowFilter] = useState(false);
    const [filterStates, setFilterStates] = useState({
      from: "",
      to: "",
      employer: "",
      employerUserId: "",
      rating: "",
      category: [],
      productId: [],
      surveyType: "all",
    });
    const [employerList, setEmployerList] = useState([]);
    const ref = useRef(null);

    const allEmployerList = useSelector(
      (state) => state.UserAuth.activeEmployerList,
    );
    const productData = useSelector((state) => state.Subscription.productData);

    useOutsideAlerter(ref, () => setShowFilter(false));

    useEffect(() => {
      dispatch(getActiveEmployers());
      dispatch(getProductList());
    }, [dispatch]);

    useEffect(() => {
      if (!activeFilters) return;
      const categoryRaw = activeFilters?.category;
      const categoryArray = Array.isArray(categoryRaw)
        ? categoryRaw
        : typeof categoryRaw === "string" && categoryRaw.trim()
          ? categoryRaw
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [];
      const productIdRaw = activeFilters?.productId;
      const productIdArray = Array.isArray(productIdRaw)
        ? productIdRaw
        : typeof productIdRaw === "string" && productIdRaw.trim()
          ? productIdRaw
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean)
          : [];
      const nextState = {
        from: activeFilters?.from ?? "",
        to: activeFilters?.to ?? "",
        employer: activeFilters?.employer ?? "",
        employerUserId: activeFilters?.employerUserId ?? "",
        rating: activeFilters?.rating ?? "",
        category: categoryArray,
        productId: productIdArray,
        surveyType: activeFilters?.surveyType ?? "all",
      };

      setFilterStates((prev) => {
        const isSame =
          prev.from === nextState.from &&
          prev.to === nextState.to &&
          prev.employer === nextState.employer &&
          prev.employerUserId === nextState.employerUserId &&
          prev.rating === nextState.rating &&
          prev.category?.length === nextState.category?.length &&
          (prev.category || []).every(
            (c, i) => (nextState.category || [])[i] === c,
          ) &&
          prev.productId?.length === nextState.productId?.length &&
          (prev.productId || []).every(
            (p, i) => (nextState.productId || [])[i] === p,
          ) &&
          prev.surveyType === nextState.surveyType;

        return isSame ? prev : nextState;
      });
    }, [
      activeFilters?.employer,
      activeFilters?.employerUserId,
      activeFilters?.from,
      activeFilters?.rating,
      activeFilters?.to,
      activeFilters?.category,
      activeFilters?.productId,
      activeFilters?.surveyType,
    ]);

    useEffect(() => {
      const tmpArray = [
        {
          label: "Select Employer",
          value: "",
          code: "",
        },
      ];
      allEmployerList &&
        allEmployerList.length > 0 &&
        allEmployerList.forEach((element) => {
          if (element?.employer !== "") {
            tmpArray.push({
              value: element?._id || "",
              label: `${capitalizeAll(element?.employer || "")}`,
              code: element?.code || "",
            });
          }
        });
      setEmployerList(tmpArray);
    }, [allEmployerList]);

    const ALLOWED_PRODUCTS = ["incbot", "incscreen", "incvid", "incfeed"];

    const productListOptions = React.useMemo(() => {
      const options = [{ label: "Select Product", value: "" }];
      if (productData?.length > 0) {
        productData
          .filter(
            (item) =>
              item?.name &&
              ALLOWED_PRODUCTS.includes(item.name.toLowerCase().trim()),
          )
          .forEach((item) => {
            options.push({
              label: item.name,
              value: item._id || "",
            });
          });
      }
      return options;
    }, [productData]);

    const handleShowFilter = () => {
      setShowFilter(!showFilter);
    };

    const formatFiltersForParent = (state) => {
      const {
        from,
        to,
        employer,
        employerUserId,
        rating,
        category,
        productId,
        surveyType,
      } = state;
      const categoryStr =
        Array.isArray(category) && category.length > 0
          ? category.join(",")
          : "";
      const productIdStr =
        Array.isArray(productId) && productId.length > 0
          ? productId.join(",")
          : "";

      return {
        from: from
          ? moment(from).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss")
          : "",
        to: to ? moment(to).endOf("day").format("YYYY-MM-DD[T]HH:mm:ss") : "",
        user: employerUserId || "",
        employer: employer || "",
        rating:
          rating === null || rating === undefined ? "" : String(rating).trim(),
        category: categoryStr,
        productId: productIdStr,
        surveyType: surveyType || "all",
      };
    };

    const handleFilterChange = (key, value) => {
      const updatedFilters = {
        ...filterStates,
        [key]: value,
      };
      setFilterStates(updatedFilters);
      setFilters(formatFiltersForParent(updatedFilters));
    };

    const handleSelectChange = (selectedOption, name) => {
      const employerValue = selectedOption?.code || "";
      const employerUserId = selectedOption?.value || "";
      const updatedFilters = {
        ...filterStates,
        [name]: employerValue,
        employerUserId,
      };
      setFilterStates(updatedFilters);
      setFilters(formatFiltersForParent(updatedFilters));
    };

    const isAnyFieldNotEmpty = () => {
      return Object.entries(filterStates).some(([key, value]) => {
        if (key === "category" || key === "productId") {
          return Array.isArray(value) && value.length > 0;
        }
        if (key === "surveyType") {
          return value && value !== "all";
        }
        return value !== null && String(value).trim() !== "";
      });
    };

    const handleCategoryToggle = (categoryValue) => {
      const current = filterStates.category || [];
      const next = current.includes(categoryValue)
        ? current.filter((c) => c !== categoryValue)
        : [...current, categoryValue];
      const updatedFilters = { ...filterStates, category: next };
      setFilterStates(updatedFilters);
      setFilters(formatFiltersForParent(updatedFilters));
    };

    const handleProductToggle = (productValue) => {
      const current = filterStates.productId || [];
      const next = current.includes(productValue)
        ? current.filter((p) => p !== productValue)
        : [...current, productValue];
      const updatedFilters = { ...filterStates, productId: next };
      setFilterStates(updatedFilters);
      setFilters(formatFiltersForParent(updatedFilters));
    };

    const handleClearFilter = () => {
      if (!isAnyFieldNotEmpty()) return;

      const clearedFilters = {
        from: "",
        to: "",
        employer: "",
        employerUserId: "",
        rating: "",
        category: [],
        productId: [],
        surveyType: "all",
      };
      setFilterStates(clearedFilters);
      setFilters(formatFiltersForParent(clearedFilters));
      setShowFilter(false);
    };

    return (
      <div className="filter-btn-container position-relative ms-2" ref={ref}>
        <button className="filter-btn d-center" onClick={handleShowFilter}>
          <img
            src={filterIcon}
            alt="filter-btn"
            className="filter-img img-fluid"
          />
        </button>
        {showFilter && (
          <div className="position-absolute filter-btn-popup input-shadow rounded f-reg platformSurvey h-">
            <div className="date-container f-reg d-flex gap-3">
              <label htmlFor="" className="f-l">
                <span className="f-sem-bol no-wrap">From</span>
                <div
                  className="filter-date-container input-shadow"
                  onClick={() => {
                    if (fromDateRef.current) {
                      fromDateRef.current.showPicker();
                    }
                  }}
                >
                  <input
                    ref={fromDateRef}
                    type="date"
                    max={filterStates?.to ?? ""}
                    value={filterStates?.from ?? ""}
                    className="filter-input"
                    onChange={(e) => handleFilterChange("from", e.target.value)}
                  />
                  <div className="filter-date-placeholder f-med">
                    {filterStates?.from
                      ? moment(filterStates.from).format("DD-MM-YYYY")
                      : "Select a date"}
                  </div>
                  <img
                    src={filterDateLogo}
                    alt="filter date logo"
                    className="filter-date-logo"
                  />
                </div>
              </label>
              <label htmlFor="" className="f-l">
                <span className="f-sem-bol no-wrap">To</span>
                <div
                  className="filter-date-container input-shadow"
                  onClick={() => {
                    if (toDateRef.current) {
                      toDateRef.current.showPicker();
                    }
                  }}
                >
                  <input
                    ref={toDateRef}
                    type="date"
                    min={filterStates?.from ?? ""}
                    value={filterStates?.to ?? ""}
                    className="filter-input"
                    onChange={(e) => handleFilterChange("to", e.target.value)}
                  />
                  <div className="filter-date-placeholder f-med">
                    {filterStates?.to
                      ? moment(filterStates.to).format("DD-MM-YYYY")
                      : "Select a date"}
                  </div>
                  <img
                    src={filterDateLogo}
                    alt="filter date logo"
                    className="filter-date-logo"
                  />
                </div>
              </label>
            </div>

            <SearchSelect
              name="employer"
              value={filterStates?.employerUserId || ""}
              label="Employer"
              onChange={(e) => handleSelectChange(e, "employer")}
              options={employerList}
              labelClass="mb-1 mt-2 f-med title f-l"
              inputClass="f-12 f-sem-bol input-shadow rounded"
              borderNone
              placeholder="Select a Employer"
            />

            <div className="mb-1 mt-2 f-med title f-l">
              <span className="f-sem-bol no-wrap d-block mb-1">Product</span>
              {productListOptions
                .filter((opt) => opt.value !== "")
                .map((opt) => (
                  <label
                    htmlFor={`product-${opt.value}`}
                    key={opt.value}
                    className="f-l d-flex align-items-center mb-1"
                  >
                    <input
                      id={`product-${opt.value}`}
                      type="checkbox"
                      value={opt.value}
                      checked={(filterStates?.productId || []).includes(
                        opt.value,
                      )}
                      onChange={() => handleProductToggle(opt.value)}
                    />
                    <span className="ms-1">{opt.label}</span>
                  </label>
                ))}
            </div>

            <div className="mb-1 mt-2 f-med title f-l">
              <label htmlFor="rating" className="f-l w-100">
                <span className="f-sem-bol no-wrap">Rating (0-5)</span>
                <select
                  id="rating"
                  className="f-12 f-med platform-survey-rating-select"
                  value={filterStates?.rating ?? ""}
                  onChange={(e) => handleFilterChange("rating", e.target.value)}
                >
                  <option value="">All</option>
                  {[0, 1, 2, 3, 4, 5].map((rating) => (
                    <option key={rating} value={rating}>
                      {rating}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {showDsatCsatDropdown && (
              <div className="mb-1 mt-2 f-med title f-l">
                <label htmlFor="survey-type" className="f-l w-100">
                  <span className="f-sem-bol no-wrap d-block mb-1">
                    Survey Type
                  </span>
                  <select
                    id="survey-type"
                    className="f-12 f-med platform-survey-rating-select"
                    value={filterStates?.surveyType ?? "all"}
                    onChange={(e) =>
                      handleFilterChange("surveyType", e.target.value)
                    }
                  >
                    {DSAT_CSAT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            {showNpsCategory && (
              <div className="mb-1 mt-2 f-med title f-l">
                <span className="f-sem-bol no-wrap d-block mb-1">
                  NPS Category
                </span>
                {NPS_CATEGORIES.map((opt) => (
                  <label
                    htmlFor={`nps-category-${opt.value}`}
                    key={opt.value}
                    className="f-l d-flex align-items-center mb-1"
                  >
                    <input
                      id={`nps-category-${opt.value}`}
                      type="checkbox"
                      value={opt.value}
                      checked={(filterStates?.category || []).includes(
                        opt.value,
                      )}
                      onChange={() => handleCategoryToggle(opt.value)}
                    />
                    <span className="ms-1">{opt.label}</span>
                  </label>
                ))}
              </div>
            )}

            <button
              className={`filter-clear-btn d-flex align-items-center justify-content-center ${
                isAnyFieldNotEmpty() ? "" : "disabled"
              }`}
              onClick={handleClearFilter}
            >
              Clear Filter
            </button>
          </div>
        )}
      </div>
    );
  },
);

export default PlatformSurveyFilterBtn;
